/**
 * Agenda LeadCEM — experiencia elite (patrón CavernicolApp AgendaCalendarUnified).
 * Mes / Semana / Día + filtros tipo Dashboard + conexiones Google/Outlook/Calendly.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Calendar, Plus, Link2, AlertTriangle, Ban, ChevronLeft, ChevronRight,
  CalendarDays, Filter, RotateCcw, Users, ExternalLink, CheckCircle2, ClipboardList,
  User, Phone, Building2, Clock,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  startOfMonth, endOfMonth, startOfWeek, endOfWeek, addDays, addMonths, addWeeks,
  format, isSameDay, isSameMonth, isToday, parseISO,
} from 'date-fns';
import { es } from 'date-fns/locale';

const WEEKDAY_LABELS = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'];
const DAY_START = 7;
const DAY_END = 21;

const STATUS_CHIP = {
  scheduled: 'bg-sky-500/20 text-sky-300 border-sky-500/30',
  completed: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30',
  cancelled: 'bg-zinc-500/20 text-zinc-400 border-zinc-500/30 line-through',
  canceled: 'bg-zinc-500/20 text-zinc-400 border-zinc-500/30 line-through',
  no_show: 'bg-rose-500/20 text-rose-300 border-rose-500/30',
  rescheduled: 'bg-amber-500/20 text-amber-300 border-amber-500/30',
};

const ESTADO_ES = {
  scheduled: 'Agendada',
  completed: 'Completada',
  cancelled: 'Cancelada',
  canceled: 'Cancelada',
  no_show: 'No asistió',
  rescheduled: 'Reagendada',
};

// Rango legible en español: "Jue 25 de sep, 11:00–12:00"
function fmtRango(ini, fin, isTask) {
  try {
    const di = parseISO(ini);
    const base = format(di, "EEE d 'de' MMM, HH:mm", { locale: es });
    if (isTask || !fin) return base;
    return `${base}–${format(parseISO(fin), 'HH:mm', { locale: es })}`;
  } catch {
    return ini || '';
  }
}

function fmtMoneda(v) {
  const n = Number(v);
  if (!n) return '';
  try {
    return n.toLocaleString('es-MX', { style: 'currency', currency: 'MXN', maximumFractionDigits: 0 });
  } catch {
    return `$${n}`;
  }
}

// Etiqueta de sincronización de calendario: verde con ✓ si ya sincronizó, gris "pendiente" si no.
// Sustituye a los ids crudos de evento que antes se mostraban (inútiles para el usuario).
function SyncTag({ ok, label }) {
  return (
    <span className={`flex items-center gap-1 text-[11px] ${ok ? 'text-emerald-300' : 'text-muted-foreground'}`}>
      {ok ? <CheckCircle2 className="w-3 h-3" /> : <span className="w-3 h-3 rounded-full border border-white/20 inline-block" />}
      {label} {ok ? '✓' : '· pendiente'}
    </span>
  );
}

const emptyForm = {
  lead_id: '',
  opportunity_id: '',
  starts_at: '',
  ends_at: '',
  title: '',
  notes: '',
  assigned_to_user_id: '',
  email_template_id: '',
};

function ApptChip({ appt, onClick }) {
  const isTask = appt.kind === 'task';
  const color = isTask
    ? 'bg-amber-500/20 text-amber-200 border-amber-500/30'
    : (STATUS_CHIP[appt.status] || STATUS_CHIP.scheduled);
  let when = '';
  try { when = format(parseISO(appt.starts_at), 'HH:mm'); } catch { when = ''; }
  return (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); onClick(appt); }}
      data-testid={isTask ? `agenda-task-chip-${appt.id}` : `agenda-chip-${appt.id}`}
      className={`w-full text-left text-[10px] px-1.5 py-0.5 rounded border truncate ${color}`}
    >
      {when} {isTask ? `Tarea · ${appt.title || ''}` : (appt.title || 'Cita')}
    </button>
  );
}

function ConnectCard({ title, connected, configured, detail, onConnect, onDisconnect, connecting, testid }) {
  return (
    <div className="rounded-lg border border-white/10 bg-black/25 p-3 space-y-2" data-testid={testid}>
      <div className="flex items-center justify-between gap-2">
        <p className="text-xs font-medium text-white">{title}</p>
        {connected ? (
          <Badge className="bg-emerald-500/15 text-emerald-300 border-emerald-500/30 text-[9px]">
            <CheckCircle2 className="w-3 h-3 mr-1" /> Conectado
          </Badge>
        ) : (
          <Badge variant="outline" className="text-[9px] border-amber-500/30 text-amber-200">
            {configured ? 'Listo para conectar' : 'Falta infra'}
          </Badge>
        )}
      </div>
      {detail ? <p className="text-[10px] text-muted-foreground truncate">{detail}</p> : null}
      <div className="flex gap-2">
        {connected ? (
          onDisconnect ? (
            <Button size="sm" variant="ghost" className="h-7 text-[10px]" onClick={onDisconnect} disabled={connecting}>
              Desconectar
            </Button>
          ) : null
        ) : (
          <Button
            size="sm"
            className="h-7 text-[10px] bg-emerald-600 hover:bg-emerald-500"
            onClick={onConnect}
            disabled={connecting || !configured}
            data-testid={`${testid}-connect`}
          >
            <Link2 className="w-3 h-3 mr-1" /> Conectar
          </Button>
        )}
      </div>
    </div>
  );
}

export default function AgendaPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [view, setView] = useState('month');
  const [anchor, setAnchor] = useState(new Date());
  const [items, setItems] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [showTasks, setShowTasks] = useState(true);
  const [blocks, setBlocks] = useState([]);
  const [oauth, setOauth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [suggestOpp, setSuggestOpp] = useState(false);
  const [blockOpen, setBlockOpen] = useState(false);
  const [blockForm, setBlockForm] = useState({
    starts_at: '', ends_at: '', purpose: 'no_disponible', title: '',
  });
  const [board, setBoard] = useState({ hoy: [], en_riesgo: [], kpis: {} });
  const [filters, setFilters] = useState({
    seller_id: 'all',
    status: 'all',
  });
  const [vendors, setVendors] = useState([]);
  const [leadQuery, setLeadQuery] = useState('');
  const [leadHits, setLeadHits] = useState([]);
  const [leadName, setLeadName] = useState('');
  const [leadOpps, setLeadOpps] = useState([]);
  const [plantillas, setPlantillas] = useState([]);
  const [connectingCal, setConnectingCal] = useState(false);
  const [selected, setSelected] = useState(null);
  const [radio, setRadio] = useState(null);
  const [radioLoading, setRadioLoading] = useState(false);

  // Al abrir una cita (no tarea), traer su radiografía 360: lead + oportunidad + datos de la cita.
  useEffect(() => {
    if (!selected || selected.kind === 'task' || !selected.id) { setRadio(null); return; }
    let alive = true;
    setRadio(null);
    setRadioLoading(true);
    api.get(`/agenda/appointments/${selected.id}/radiografia`)
      .then((r) => { if (alive) setRadio(r.data); })
      .catch(() => { if (alive) setRadio(null); })
      .finally(() => { if (alive) setRadioLoading(false); });
    return () => { alive = false; };
  }, [selected]);

  const range = useMemo(() => {
    if (view === 'month') {
      return {
        from: startOfWeek(startOfMonth(anchor), { weekStartsOn: 1 }),
        to: addDays(endOfWeek(endOfMonth(anchor), { weekStartsOn: 1 }), 1),
      };
    }
    if (view === 'week') {
      return {
        from: startOfWeek(anchor, { weekStartsOn: 1 }),
        to: addDays(endOfWeek(anchor, { weekStartsOn: 1 }), 1),
      };
    }
    const d = new Date(anchor.getFullYear(), anchor.getMonth(), anchor.getDate());
    return { from: d, to: addDays(d, 1) };
  }, [view, anchor]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        date_from: range.from.toISOString(),
        date_to: range.to.toISOString(),
      };
      if (filters.seller_id && filters.seller_id !== 'all') params.assigned_to = filters.seller_id;
      if (filters.status && filters.status !== 'all') params.status = filters.status;

      const [a, b, o, br, act] = await Promise.all([
        api.get('/agenda/appointments', { params }),
        api.get('/agenda/blocks', { params: { date_from: params.date_from, date_to: params.date_to } }).catch(() => ({ data: { items: [] } })),
        api.get('/agenda/calendar-oauth-status'),
        api.get('/agenda/board').catch(() => ({ data: { hoy: [], en_riesgo: [], kpis: {} } })),
        api.get('/activities', {
          params: {
            date_field: 'appointment_date',
            date_from: format(range.from, 'yyyy-MM-dd'),
            date_to: format(addDays(range.to, -1), 'yyyy-MM-dd'),
            limit: 200,
            ...(filters.seller_id && filters.seller_id !== 'all' ? { vendor_id: filters.seller_id } : {}),
          },
        }).catch(() => ({ data: { items: [] } })),
      ]);
      const appts = a.data?.items || [];
      setItems(appts);
      setBlocks(b.data?.items || []);
      const _mergedOauth = { ...(o.data || {}) };
      try {
        const _cd = (await api.get('/integrations/calendar/status')).data || {};
        _mergedOauth.google_calendar = { configured: !!_cd.google_calendar?.platform_ready, connected: !!_cd.google_calendar?.connected, email: _cd.google_calendar?.email || '' };
        _mergedOauth.outlook_calendar = { configured: !!_cd.outlook_calendar?.platform_ready, connected: !!_cd.outlook_calendar?.connected, email: _cd.outlook_calendar?.email || '' };
      } catch (_e) { /* si falla, se queda el estado base */ }
      setOauth(_mergedOauth);
      setBoard(br.data || { hoy: [], en_riesgo: [], kpis: {} });
      const mirrored = new Set(
        appts.map((x) => x.source_activity_id).filter(Boolean),
      );
      const mapped = (act.data?.items || [])
        .filter((t) => t.appointment_date)
        .filter((t) => !mirrored.has(t.id) && !appts.some((x) => x.id === (t.extras || {}).agenda_appointment_id))
        .map((t) => ({
          id: `task-${t.id}`,
          kind: 'task',
          title: t.label || t.type,
          starts_at: `${t.appointment_date}T${(t.appointment_time || '10:00')}:00`,
          ends_at: `${t.appointment_date}T${(t.appointment_time || '10:00')}:00`,
          status: t.status || 'pending',
          lead_id: t.lead_id,
          activity_type: t.type,
        }));
      setTasks(mapped);
    } catch {
      setItems([]);
      setBlocks([]);
      setTasks([]);
    } finally {
      setLoading(false);
    }
  }, [range, filters]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    api.get('/team').then((r) => {
      const team = Array.isArray(r.data) ? r.data : [];
      setVendors(team.filter((m) => m.role !== 'superadmin'));
    }).catch(() => setVendors([]));
    api.get('/correo/plantillas').then((r) => {
      setPlantillas(Array.isArray(r.data?.plantillas) ? r.data.plantillas : []);
    }).catch(() => setPlantillas([]));
  }, []);

  // Calendly OAuth return
  useEffect(() => {
    const cal = searchParams.get('calendly');
    if (!cal) return;
    if (cal === 'success') toast.success('Calendly conectado');
    if (cal === 'error') toast.error(`Calendly: ${searchParams.get('msg') || 'error'}`);
    const next = new URLSearchParams(searchParams);
    next.delete('calendly'); next.delete('msg');
    setSearchParams(next, { replace: true });
    load();
  }, [searchParams, setSearchParams, load]);

  // Google/Outlook OAuth return (conexion por USUARIO)
  useEffect(() => {
    const g = searchParams.get('google');
    const ou = searchParams.get('outlook');
    const prov = g ? 'google' : (ou ? 'outlook' : null);
    if (!prov) return;
    const val = g || ou;
    if (val === 'success') toast.success(`${prov === 'google' ? 'Google Calendar' : 'Outlook'} conectado`);
    else toast.error(`${prov === 'google' ? 'Google' : 'Outlook'}: ${searchParams.get('msg') || 'error'}`);
    const next = new URLSearchParams(searchParams);
    next.delete('google'); next.delete('outlook'); next.delete('msg');
    setSearchParams(next, { replace: true });
    load();
  }, [searchParams, setSearchParams, load]);

  const shift = (dir) => {
    if (view === 'month') setAnchor((a) => addMonths(a, dir));
    else if (view === 'week') setAnchor((a) => addWeeks(a, dir));
    else setAnchor((a) => addDays(a, dir));
  };

  const monthDays = useMemo(() => {
    const days = [];
    let d = range.from;
    while (d < range.to) { days.push(d); d = addDays(d, 1); }
    return days;
  }, [range]);

  const apptsForDay = (day) => {
    const citas = items.filter((a) => {
      try { return isSameDay(parseISO(a.starts_at), day); } catch { return false; }
    });
    const extra = showTasks ? tasks.filter((t) => {
      try { return isSameDay(parseISO(t.starts_at), day); } catch { return false; }
    }) : [];
    return [...citas, ...extra].sort((a, b) => String(a.starts_at).localeCompare(String(b.starts_at)));
  };

  const blocksForDay = (day) => blocks.filter((b) => {
    try { return isSameDay(parseISO(b.starts_at), day); } catch { return false; }
  });

  const searchLeads = async (q) => {
    setLeadQuery(q);
    if (!q || q.trim().length < 2) { setLeadHits([]); return; }
    try {
      const r = await api.get('/leads', { params: { search: q.trim(), limit: 8 } });
      const list = Array.isArray(r.data) ? r.data : (r.data?.items || r.data?.leads || []);
      setLeadHits(list.slice(0, 8));
    } catch { setLeadHits([]); }
  };

  const pickLead = async (l) => {
    setForm((f) => ({
      ...f,
      lead_id: l.id,
      title: f.title || `Cita · ${l.name || ''}`.trim(),
      opportunity_id: '',
    }));
    setLeadQuery(l.name || l.phone || l.id);
    setLeadName(l.name || l.phone || l.id);
    setLeadHits([]);
    try {
      const r = await api.get('/opportunities');
      const list = Array.isArray(r.data) ? r.data : (r.data?.items || r.data?.opportunities || []);
      setLeadOpps(list.filter((o) => o.lead_id === l.id));
    } catch { setLeadOpps([]); }
  };

  const openCreateOnDay = (day) => {
    const start = new Date(day);
    start.setHours(10, 0, 0, 0);
    const end = new Date(day);
    end.setHours(11, 0, 0, 0);
    const toLocal = (d) => {
      const pad = (n) => String(n).padStart(2, '0');
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
    };
    setForm({
      ...emptyForm,
      starts_at: toLocal(start),
      ends_at: toLocal(end),
      title: 'Cita',
    });
    setDialogOpen(true);
  };

  const submitAppointment = async (forceWithoutOpp = false) => {
    if (!form.lead_id.trim()) { toast.error('Elige un lead'); return; }
    if (!form.starts_at || !form.ends_at) { toast.error('Indica inicio y fin'); return; }
    if (!form.opportunity_id.trim() && !forceWithoutOpp) { setSuggestOpp(true); return; }
    try {
      await api.post('/agenda/appointments', {
        ...form,
        opportunity_id: form.opportunity_id.trim(),
        force_without_opportunity: forceWithoutOpp,
        starts_at: new Date(form.starts_at).toISOString(),
        ends_at: new Date(form.ends_at).toISOString(),
      });
      toast.success('Cita creada');
      setDialogOpen(false);
      setSuggestOpp(false);
      setForm(emptyForm);
      setLeadHits([]);
      setLeadQuery('');
      setLeadName('');
      setLeadOpps([]);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo crear la cita');
    }
  };

  const submitBlock = async () => {
    try {
      await api.post('/agenda/blocks', {
        ...blockForm,
        starts_at: new Date(blockForm.starts_at).toISOString(),
        ends_at: new Date(blockForm.ends_at).toISOString(),
      });
      toast.success('Espacio bloqueado');
      setBlockOpen(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo bloquear');
    }
  };

  const handleAction = async (c, key) => {
    try {
      if (key === 'completed' || key === 'no_show' || key === 'cancelled') {
        await api.patch(`/agenda/appointments/${c.id}/status`, { status: key });
        toast.success('Actualizado');
        load();
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error');
    }
  };

  const connectCalendly = async () => {
    setConnectingCal(true);
    try {
      const r = await api.get('/calendly/connect');
      window.location.href = r.data.auth_url;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo iniciar Calendly');
      setConnectingCal(false);
    }
  };

  const disconnectCalendly = async () => {
    try {
      await api.delete('/calendly/disconnect');
      toast.success('Calendly desconectado');
      load();
    } catch { toast.error('Error al desconectar'); }
  };

  const connectGoogleOrOutlook = async (platformKey) => {
    const provider = platformKey === 'google_calendar' ? 'google' : 'outlook';
    const row = oauth?.[platformKey];
    if (!row?.configured) {
      toast.error('Este calendario aún no está configurado por la plataforma (falta client_id/secret en Super Admin → Integraciones).');
      return;
    }
    try {
      const r = await api.get(`/integrations/calendar/${provider}/connect`);
      if (r.data?.auth_url) { window.location.href = r.data.auth_url; }
      else { toast.error('No se pudo iniciar la conexión'); }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo iniciar la conexión');
    }
  };

  const disconnectCalendar = async (platformKey) => {
    const provider = platformKey === 'google_calendar' ? 'google' : 'outlook';
    try {
      await api.delete(`/integrations/calendar/${provider}/disconnect`);
      toast.success('Cuenta desconectada');
      load();
    } catch { toast.error('Error al desconectar'); }
  };

  const headerLabel = view === 'month'
    ? format(anchor, 'MMMM yyyy', { locale: es })
    : view === 'week'
      ? `Semana del ${format(startOfWeek(anchor, { weekStartsOn: 1 }), 'd MMM', { locale: es })}`
      : format(anchor, "EEEE d 'de' MMMM", { locale: es });

  const hours = [];
  for (let h = DAY_START; h <= DAY_END; h += 1) hours.push(h);

  return (
    <div className="p-4 md:p-6 space-y-4" data-testid="agenda-page">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Calendar className="w-5 h-5 text-emerald-400" />
          <h1 className="text-lg font-semibold text-white">Agenda</h1>
          <Badge variant="outline" className="text-[10px] border-white/15">
            {loading ? '…' : `${items.length} en vista`}
          </Badge>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button size="sm" variant="outline" onClick={() => setBlockOpen(true)} data-testid="agenda-block-btn">
            <Ban className="w-3.5 h-3.5 mr-1" /> Bloquear
          </Button>
          <Button size="sm" onClick={() => { setForm(emptyForm); setDialogOpen(true); }} data-testid="agenda-new-btn">
            <Plus className="w-3.5 h-3.5 mr-1" /> Nueva cita
          </Button>
        </div>
      </div>

      {/* Conexiones omnicanal */}
      <div className="grid sm:grid-cols-3 gap-2" data-testid="agenda-connections">
        <ConnectCard
          title="Google Calendar"
          configured={!!oauth?.google_calendar?.configured}
          connected={!!oauth?.google_calendar?.connected}
          detail={oauth?.google_calendar?.connected ? (oauth?.google_calendar?.email || 'Tu cuenta conectada') : (oauth?.google_calendar?.configured ? 'Conecta tu propia cuenta de Google' : 'Falta configurar en Super Admin')}
          onConnect={() => connectGoogleOrOutlook('google_calendar')}
          onDisconnect={oauth?.google_calendar?.connected ? () => disconnectCalendar('google_calendar') : undefined}
          testid="agenda-google"
        />
        <ConnectCard
          title="Outlook"
          configured={!!oauth?.outlook_calendar?.configured}
          connected={!!oauth?.outlook_calendar?.connected}
          detail={oauth?.outlook_calendar?.connected ? (oauth?.outlook_calendar?.email || 'Tu cuenta conectada') : (oauth?.outlook_calendar?.configured ? 'Conecta tu propia cuenta de Outlook' : 'Falta configurar en Super Admin')}
          onConnect={() => connectGoogleOrOutlook('outlook_calendar')}
          onDisconnect={oauth?.outlook_calendar?.connected ? () => disconnectCalendar('outlook_calendar') : undefined}
          testid="agenda-outlook"
        />
        <ConnectCard
          title="Calendly"
          configured
          connected={!!oauth?.calendly?.connected}
          detail={oauth?.calendly?.email || oauth?.calendly?.scheduling_url || 'Agenda pública + OAuth'}
          onConnect={connectCalendly}
          onDisconnect={disconnectCalendly}
          connecting={connectingCal}
          testid="agenda-calendly"
        />
      </div>

      {/* Filtros tipo Dashboard */}
      <div className="flex flex-wrap items-center gap-2 p-3 rounded-lg bg-card/40 border border-white/5" data-testid="agenda-filters">
        <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <Filter className="w-3.5 h-3.5" /> Filtros
        </div>
        <div className="flex items-center gap-1 min-w-[160px]">
          <Users className="w-3.5 h-3.5 text-muted-foreground" />
          <Select value={filters.seller_id} onValueChange={(v) => setFilters((f) => ({ ...f, seller_id: v }))}>
            <SelectTrigger className="h-7 bg-black/20 border-white/10 text-[11px]" data-testid="agenda-filter-seller">
              <SelectValue placeholder="Vendedor" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos los vendedores</SelectItem>
              {vendors.map((v) => (
                <SelectItem key={v.id} value={v.id}>{v.name || v.email}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <Select value={filters.status} onValueChange={(v) => setFilters((f) => ({ ...f, status: v }))}>
          <SelectTrigger className="h-7 w-[140px] bg-black/20 border-white/10 text-[11px]" data-testid="agenda-filter-status">
            <SelectValue placeholder="Estado" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos los estados</SelectItem>
            <SelectItem value="scheduled">Programada</SelectItem>
            <SelectItem value="completed">Completada</SelectItem>
            <SelectItem value="no_show">No-show</SelectItem>
            <SelectItem value="cancelled">Cancelada</SelectItem>
            <SelectItem value="rescheduled">Reagendada</SelectItem>
          </SelectContent>
        </Select>
        <Button size="sm" variant="ghost" className="h-7 text-[10px]" onClick={() => setFilters({ seller_id: 'all', status: 'all' })}>
          <RotateCcw className="w-3 h-3 mr-1" /> Limpiar
        </Button>
      </div>

      {/* KPIs compactos */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2" data-testid="agenda-kpis">
        {[
          ['Show', board.kpis?.completed],
          ['No-show', board.kpis?.no_show],
          ['Reagendadas', board.kpis?.rescheduled],
          ['Programadas', board.kpis?.scheduled],
        ].map(([lab, val]) => (
          <Card key={lab} className="bg-card/40 border-white/5">
            <CardContent className="p-3">
              <p className="text-[10px] uppercase text-muted-foreground">{lab}</p>
              <p className="text-lg font-semibold text-white">{loading ? '—' : (val ?? 0)}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Toolbar calendario */}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-1">
          <Button size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={() => shift(-1)} data-testid="agenda-prev">
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <Button size="sm" variant="ghost" className="h-8 text-xs" onClick={() => setAnchor(new Date())}>Hoy</Button>
          <Button size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={() => shift(1)} data-testid="agenda-next">
            <ChevronRight className="w-4 h-4" />
          </Button>
          <p className="text-sm text-white font-medium capitalize ml-2">{headerLabel}</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setShowTasks((v) => !v)}
            data-testid="agenda-toggle-tasks"
            className={`h-8 px-3 text-[11px] rounded-lg border flex items-center gap-1 ${
              showTasks ? 'bg-amber-500/20 text-amber-200 border-amber-500/30' : 'bg-black/20 text-muted-foreground border-white/10'
            }`}
          >
            <ClipboardList className="w-3.5 h-3.5" /> Tareas {showTasks ? tasks.length : ''}
          </button>
          <div className="flex rounded-lg border border-white/10 overflow-hidden" data-testid="agenda-view-toggle">
          {['month', 'week', 'day'].map((v) => (
            <button
              key={v}
              type="button"
              onClick={() => setView(v)}
              className={`px-3 h-8 text-[11px] ${view === v ? 'bg-emerald-600 text-white' : 'bg-black/20 text-muted-foreground hover:text-white'}`}
              data-testid={`agenda-view-${v}`}
            >
              {v === 'month' ? 'Mes' : v === 'week' ? 'Semana' : 'Día'}
            </button>
          ))}
          </div>
        </div>
      </div>

      {/* Grid Mes */}
      {view === 'month' && (
        <Card className="bg-card/40 border-white/5 overflow-hidden" data-testid="agenda-month-grid">
          <div className="grid grid-cols-7 border-b border-white/5">
            {WEEKDAY_LABELS.map((d) => (
              <div key={d} className="px-2 py-1.5 text-[10px] uppercase text-muted-foreground text-center">{d}</div>
            ))}
          </div>
          <div className="grid grid-cols-7 auto-rows-[minmax(88px,1fr)]">
            {monthDays.map((day) => {
              const inMonth = isSameMonth(day, anchor);
              const list = apptsForDay(day);
              const bl = blocksForDay(day);
              return (
                <button
                  type="button"
                  key={day.toISOString()}
                  onClick={() => openCreateOnDay(day)}
                  className={`border border-white/5 p-1 text-left align-top hover:bg-white/5 transition ${
                    isToday(day) ? 'bg-emerald-500/5' : ''
                  } ${!inMonth ? 'opacity-40' : ''}`}
                  data-testid={`agenda-day-${format(day, 'yyyy-MM-dd')}`}
                >
                  <div className={`text-[11px] mb-1 ${isToday(day) ? 'text-emerald-300 font-semibold' : 'text-muted-foreground'}`}>
                    {format(day, 'd')}
                  </div>
                  <div className="space-y-0.5">
                    {bl.slice(0, 1).map((b) => (
                      <div key={b.id} className="text-[9px] px-1 rounded bg-zinc-500/20 text-zinc-300 truncate">
                        🚫 {b.title || b.purpose}
                      </div>
                    ))}
                    {list.slice(0, 3).map((a) => (
                      <ApptChip key={a.id} appt={a} onClick={setSelected} />
                    ))}
                    {list.length > 3 && (
                      <p className="text-[9px] text-muted-foreground px-1">+{list.length - 3} más</p>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        </Card>
      )}

      {/* Grid Semana / Día */}
      {(view === 'week' || view === 'day') && (
        <Card className="bg-card/40 border-white/5 overflow-auto" data-testid={`agenda-${view}-grid`}>
          <div className={`grid ${view === 'week' ? 'grid-cols-8' : 'grid-cols-2'} min-w-[640px]`}>
            <div className="sticky left-0 bg-zinc-950/80 border-r border-white/5" />
            {(view === 'day' ? [anchor] : monthDays.slice(0, 7)).map((day) => (
              <div key={day.toISOString()} className="px-2 py-2 text-center border-b border-white/5">
                <p className="text-[10px] uppercase text-muted-foreground">{format(day, 'EEE', { locale: es })}</p>
                <p className={`text-sm ${isToday(day) ? 'text-emerald-300 font-semibold' : 'text-white'}`}>{format(day, 'd')}</p>
              </div>
            ))}
            {hours.map((h) => (
              <div key={`row-${h}`} className="contents">
                <div className="sticky left-0 bg-zinc-950/80 border-r border-white/5 px-2 py-3 text-[10px] text-muted-foreground">
                  {String(h).padStart(2, '0')}:00
                </div>
                {(view === 'day' ? [anchor] : monthDays.slice(0, 7)).map((day) => {
                  const slotAppts = apptsForDay(day).filter((a) => {
                    try { return parseISO(a.starts_at).getHours() === h; } catch { return false; }
                  });
                  return (
                    <button
                      type="button"
                      key={`${day.toISOString()}-${h}`}
                      onClick={() => {
                        const d = new Date(day);
                        d.setHours(h, 0, 0, 0);
                        openCreateOnDay(d);
                      }}
                      className="border border-white/5 min-h-[44px] p-0.5 hover:bg-white/5 text-left"
                    >
                      {slotAppts.map((a) => (
                        <ApptChip key={a.id} appt={a} onClick={setSelected} />
                      ))}
                    </button>
                  );
                })}
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Hoy / En riesgo (operación omnicanal) */}
      <div className="grid md:grid-cols-2 gap-4">
        <Card className="bg-card/50 border-white/5" data-testid="agenda-hoy">
          <CardContent className="p-4 space-y-2">
            <p className="text-xs font-semibold text-white flex items-center gap-1">
              <CalendarDays className="w-3.5 h-3.5 text-emerald-400" /> Hoy
            </p>
            {(board.hoy || []).length === 0 && <p className="text-[11px] text-muted-foreground">Nada para hoy.</p>}
            {(board.hoy || []).map((c) => (
              <div key={c.id} className="rounded-lg border border-white/10 p-2 text-xs space-y-1">
                <div className="flex justify-between">
                  <span className="text-white font-medium">{c.title}</span>
                  <Badge variant="outline" className="text-[9px]">{c.status}</Badge>
                </div>
                <p className="text-muted-foreground">{c.starts_at}</p>
                <div className="flex flex-wrap gap-1">
                  <Button size="sm" variant="secondary" className="h-6 text-[10px]" onClick={() => navigate(`/leads?highlight=${c.lead_id}`)}>
                    Lead
                  </Button>
                  {(c.actions || []).map((act) => (
                    <Button key={act.key} size="sm" variant="secondary" className="h-6 text-[10px]" onClick={() => handleAction(c, act.key)}>
                      {act.label}
                    </Button>
                  ))}
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
        <Card className="bg-card/50 border-amber-500/20" data-testid="agenda-en-riesgo">
          <CardContent className="p-4 space-y-2">
            <p className="text-xs font-semibold text-amber-200 flex items-center gap-1">
              <AlertTriangle className="w-3.5 h-3.5" /> En riesgo / No-show
            </p>
            {(board.en_riesgo || []).length === 0 && <p className="text-[11px] text-muted-foreground">Sin riesgos.</p>}
            {(board.en_riesgo || []).map((c) => (
              <div key={c.id} className="rounded-lg border border-amber-500/20 p-2 text-xs space-y-1">
                <div className="flex justify-between">
                  <span className="text-white font-medium">{c.title}</span>
                  <Badge variant="outline" className="text-[9px]">{c.status}</Badge>
                </div>
                <div className="flex flex-wrap gap-1">
                  {(c.actions || []).map((act) => (
                    <Button key={act.key} size="sm" variant="secondary" className="h-6 text-[10px]" onClick={() => handleAction(c, act.key)}>
                      {act.label}
                    </Button>
                  ))}
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>

      {/* Detalle cita */}
      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="bg-zinc-950 border-white/10 max-w-lg" data-testid="agenda-detail">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {selected?.kind === 'task'
                ? <ClipboardList className="w-4 h-4 text-amber-300" />
                : <Calendar className="w-4 h-4 text-sky-300" />}
              {selected?.kind === 'task' ? `Tarea · ${selected?.title || ''}` : (selected?.title || 'Cita')}
            </DialogTitle>
          </DialogHeader>
          {selected && (
            <div className="space-y-3 text-xs">
              {/* Cuándo + estado */}
              <div className="flex items-center justify-between gap-2">
                <span className="flex items-center gap-1.5 text-white">
                  <Clock className="w-3.5 h-3.5 text-muted-foreground" />
                  {fmtRango(selected.starts_at, selected.ends_at, selected.kind === 'task')}
                </span>
                <Badge className={STATUS_CHIP[selected.status] || STATUS_CHIP.scheduled}>
                  {ESTADO_ES[selected.status] || selected.status}
                </Badge>
              </div>
              {selected.kind === 'task' && selected.activity_type && (
                <p className="text-muted-foreground"><span className="text-white">Tipo:</span> {selected.activity_type}</p>
              )}

              {/* Radiografía 360 (solo citas) */}
              {selected.kind !== 'task' && (
                <>
                  {radioLoading && <p className="text-muted-foreground">Cargando radiografía…</p>}
                  {radio && (
                    <div className="space-y-3">
                      {/* Lead */}
                      {radio.lead && (
                        <div className="rounded-lg border border-white/10 bg-white/[0.03] p-3 space-y-1.5">
                          <p className="text-[10px] uppercase tracking-wide text-muted-foreground flex items-center gap-1"><User className="w-3 h-3" /> Lead</p>
                          <p className="text-sm font-semibold text-white">{radio.lead.name || 'Sin nombre'}</p>
                          <div className="flex flex-wrap gap-x-4 gap-y-1 text-muted-foreground">
                            {radio.lead.phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3" /> {radio.lead.phone}</span>}
                            {radio.lead.email && <span>{radio.lead.email}</span>}
                          </div>
                          {(radio.lead.calificacion || radio.lead.interes) && (
                            <div className="flex flex-wrap gap-1.5 pt-1">
                              {radio.lead.calificacion && <Badge className="bg-emerald-500/15 text-emerald-300 border-emerald-500/25">{radio.lead.calificacion}</Badge>}
                              {radio.lead.interes && <Badge className="bg-amber-500/15 text-amber-200 border-amber-500/25">Interés: {radio.lead.interes}</Badge>}
                            </div>
                          )}
                        </div>
                      )}
                      {/* Oportunidad */}
                      {radio.oportunidad ? (
                        <div className="rounded-lg border border-white/10 bg-white/[0.03] p-3 space-y-1.5">
                          <p className="text-[10px] uppercase tracking-wide text-muted-foreground flex items-center gap-1"><Building2 className="w-3 h-3" /> Oportunidad</p>
                          <div className="flex items-center justify-between">
                            <span className="text-white">{radio.oportunidad.etapa || 'Sin etapa'}</span>
                            {radio.oportunidad.valor ? <span className="gold-accent font-bold">{fmtMoneda(radio.oportunidad.valor)}</span> : null}
                          </div>
                          {radio.oportunidad.estado && <p className="text-muted-foreground">Estado: {radio.oportunidad.estado}</p>}
                        </div>
                      ) : (
                        <p className="text-amber-300 flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> Sin oportunidad ligada</p>
                      )}
                      {/* Vendedor + notas */}
                      <div className="space-y-1 text-muted-foreground">
                        {radio.cita?.vendedor && <p className="flex items-center gap-1"><Users className="w-3 h-3" /> <span className="text-white">Atiende:</span> {radio.cita.vendedor}</p>}
                        {radio.cita?.notes && <p><span className="text-white">Notas:</span> {radio.cita.notes}</p>}
                      </div>
                      {/* Sincronización de calendarios */}
                      <div className="flex flex-wrap gap-4 pt-2 border-t border-white/5">
                        <SyncTag ok={radio.cita?.synced_google} label="Google Calendar" />
                        <SyncTag ok={radio.cita?.synced_outlook} label="Outlook" />
                      </div>
                    </div>
                  )}
                </>
              )}

              <DialogFooter className="gap-2 sm:gap-2 pt-1">
                {selected.lead_id && (
                  <Button size="sm" variant="outline" onClick={() => navigate(`/leads?open=${selected.lead_id}`)}>
                    <ExternalLink className="w-3 h-3 mr-1" /> Ver lead
                  </Button>
                )}
                {selected.kind !== 'task' && (
                  <Button size="sm" onClick={() => { handleAction(selected, 'completed'); setSelected(null); }}>
                    <CheckCircle2 className="w-3 h-3 mr-1" /> Completar
                  </Button>
                )}
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Nueva cita */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-zinc-950 border-white/10 max-w-md" data-testid="agenda-create-dialog">
          <DialogHeader>
            <DialogTitle>Nueva cita</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Buscar lead *</Label>
              <Input
                value={leadQuery}
                onChange={(e) => searchLeads(e.target.value)}
                placeholder="Nombre, teléfono o email"
                data-testid="agenda-lead-search"
              />
              {form.lead_id && (
                <p className="text-[10px] text-emerald-300 mt-1">Lead: {leadName || form.lead_id}</p>
              )}
              {leadHits.length > 0 && (
                <div className="mt-1 rounded border border-white/10 bg-black/40 max-h-36 overflow-auto">
                  {leadHits.map((l) => (
                    <button
                      type="button"
                      key={l.id}
                      className="w-full text-left px-2 py-1.5 text-[11px] hover:bg-white/5 text-white"
                      onClick={() => pickLead(l)}
                    >
                      {(l.name || 'Sin nombre')} · {l.phone || l.email || l.id}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div>
              <Label>Oportunidad (opcional)</Label>
              <Select
                value={form.opportunity_id || 'none'}
                onValueChange={(v) => setForm({ ...form, opportunity_id: v === 'none' ? '' : v })}
              >
                <SelectTrigger className="bg-black/20 border-white/10 text-xs" data-testid="agenda-opp-id">
                  <SelectValue placeholder="Sin oportunidad" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Sin oportunidad</SelectItem>
                  {leadOpps.map((o) => (
                    <SelectItem key={o.id} value={o.id}>{o.title || o.name || o.id}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {form.lead_id && leadOpps.length === 0 && (
                <p className="text-[10px] text-muted-foreground mt-1">Este lead no tiene oportunidades. Puedes crear la cita igual.</p>
              )}
            </div>
            <div>
              <Label>Plantilla de correo (opcional)</Label>
              <Select
                value={form.email_template_id || 'none'}
                onValueChange={(v) => setForm({ ...form, email_template_id: v === 'none' ? '' : v })}
              >
                <SelectTrigger className="bg-black/20 border-white/10 text-xs" data-testid="agenda-email-tpl">
                  <SelectValue placeholder="No enviar correo" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No enviar correo</SelectItem>
                  {plantillas.map((p) => (
                    <SelectItem key={p.id} value={p.id}>{p.nombre || p.id}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Título</Label>
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
            </div>
            <div>
              <Label>Notas</Label>
              <Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Motivo, ubicación…" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label>Inicio</Label>
                <Input type="datetime-local" value={form.starts_at} onChange={(e) => setForm({ ...form, starts_at: e.target.value })} data-testid="agenda-starts" />
              </div>
              <div>
                <Label>Fin</Label>
                <Input type="datetime-local" value={form.ends_at} onChange={(e) => setForm({ ...form, ends_at: e.target.value })} data-testid="agenda-ends" />
              </div>
            </div>
            <div>
              <Label>Vendedor (opcional — por defecto dueño del lead)</Label>
              <Select
                value={form.assigned_to_user_id || 'auto'}
                onValueChange={(v) => setForm({ ...form, assigned_to_user_id: v === 'auto' ? '' : v })}
              >
                <SelectTrigger className="bg-black/20 border-white/10 text-xs">
                  <SelectValue placeholder="Auto (dueño del lead)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="auto">Auto (dueño del lead)</SelectItem>
                  {vendors.map((v) => (
                    <SelectItem key={v.id} value={v.id}>{v.name || v.email}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter className="gap-2">
            {suggestOpp && (
              <>
                <Button size="sm" variant="outline" onClick={() => navigate(`/leads?highlight=${form.lead_id}`)} data-testid="agenda-go-lead">
                  Ir a la ficha del lead
                </Button>
                <Button size="sm" variant="secondary" onClick={() => submitAppointment(true)} data-testid="agenda-force-no-opp">
                  Crear sin oportunidad
                </Button>
              </>
            )}
            <Button size="sm" onClick={() => submitAppointment(false)} data-testid="agenda-save">Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bloqueo */}
      <Dialog open={blockOpen} onOpenChange={setBlockOpen}>
        <DialogContent className="bg-zinc-950 border-white/10 max-w-md">
          <DialogHeader>
            <DialogTitle>Bloquear espacio</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label>Inicio</Label>
              <Input type="datetime-local" value={blockForm.starts_at} onChange={(e) => setBlockForm({ ...blockForm, starts_at: e.target.value })} />
            </div>
            <div>
              <Label>Fin</Label>
              <Input type="datetime-local" value={blockForm.ends_at} onChange={(e) => setBlockForm({ ...blockForm, ends_at: e.target.value })} />
            </div>
          </div>
          <div>
            <Label>Título</Label>
            <Input value={blockForm.title} onChange={(e) => setBlockForm({ ...blockForm, title: e.target.value })} placeholder="No disponible" />
          </div>
          <DialogFooter>
            <Button size="sm" onClick={submitBlock}>Guardar bloqueo</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
